/**
 * Memory markers — internal API helpers.
 * User-facing label: People, places & things
 */

import axios from 'axios';
import { MapPin, Package, Flag, Calendar, Gem } from 'lucide-react';
import { getApiBase } from './apiBase';

function apiBase() {
  return getApiBase();
}

/** User-facing section title (never "items" / "possessions" in cold wording). */
export const MEMORY_MARKERS_UI_LABEL = 'People, places & things';

export const PRICELESS_POSSESSIONS_LABEL = 'Priceless Possessions';

export const PRICELESS_POSSESSIONS_TAGLINE =
  'Preserve the objects, keepsakes and everyday things that carry family memory.';

export const PRICELESS_POSSESSIONS_HEADLINE = 'Every object has a story.';

export const PRICELESS_POSSESSIONS_NARRATIVE =
  'Not every memory is a portrait. Some memories live in a watch, a chair, a recipe book, a favourite hat, or an ornament kept for years.';

export const CONNECTED_MEMORIES_LABEL = 'Connected memories';

export const POSSESSION_MARKER_TYPES = ['object', 'keepsake'];

export const MARKER_TYPE_LABELS = {
  place: 'Place',
  object: 'Keepsake',
  keepsake: 'Keepsake',
  milestone: 'Milestone',
  event: 'Event',
};

export const POSSESSION_CATEGORY_LABELS = {
  jewellery: 'Jewellery',
  clothing: 'Clothing',
  recipe: 'Recipe book',
  furniture: 'Furniture',
  ornament: 'Ornament',
  tool: 'Tool',
  toy: 'Toy',
  photograph: 'Photograph',
  letter: 'Letter',
  document: 'Document',
  other: 'Treasured thing',
};

export const POSSESSION_PRIVACY_LABELS = {
  private: 'Private',
  shared: 'Shared with family',
  public_later: 'May share later',
};

/** Keepsake uses `object` (Priceless Possessions use `keepsake` via possessionEmptyPayload). */
export const MARKER_TYPE_ORDER = ['place', 'object', 'milestone', 'event'];

export const MARKER_TYPE_ICONS = {
  place: MapPin,
  object: Gem,
  keepsake: Gem,
  milestone: Flag,
  event: Calendar,
};

/** Distinct timeline / card accent colors per marker type */
export const MARKER_TYPE_STYLES = {
  place: {
    dot: 'bg-sky-400',
    iconBg: 'bg-sky-50 group-hover:bg-sky-100/80',
    iconText: 'text-sky-600',
  },
  object: {
    dot: 'bg-amber-500',
    iconBg: 'bg-amber-50 group-hover:bg-amber-100/80',
    iconText: 'text-amber-700',
  },
  milestone: {
    dot: 'bg-violet-400',
    iconBg: 'bg-violet-50 group-hover:bg-violet-100/80',
    iconText: 'text-violet-600',
  },
  event: {
    dot: 'bg-teal-400',
    iconBg: 'bg-teal-50 group-hover:bg-teal-100/80',
    iconText: 'text-teal-600',
  },
};

export const ATTACHMENT_MODE_LABELS = {
  person: 'Part of someone\u2019s story',
  couple: 'Shared by a couple',
  family: 'Family-wide',
  timeline_only: 'Archive timeline only',
};

export async function fetchMemoryMarkersForTree(treeId, { personId = null, markerType = null, possessionsOnly = false } = {}) {
  if (!treeId) return [];
  const params = new URLSearchParams();
  if (personId) params.set('person_id', personId);
  if (markerType) params.set('marker_type', markerType);
  if (possessionsOnly) params.set('possessions_only', 'true');
  const qs = params.toString();
  const url = `${apiBase()}/trees/${encodeURIComponent(treeId)}/memory-markers${qs ? `?${qs}` : ''}`;
  const { data } = await axios.get(url);
  return Array.isArray(data) ? data : [];
}

export async function createMemoryMarker(treeId, payload) {
  const { data } = await axios.post(`${apiBase()}/trees/${encodeURIComponent(treeId)}/memory-markers`, payload);
  return data;
}

export async function updateMemoryMarker(markerId, payload) {
  const { data } = await axios.patch(`${apiBase()}/memory-markers/${encodeURIComponent(markerId)}`, payload);
  return data;
}

export async function deleteMemoryMarker(markerId) {
  await axios.delete(`${apiBase()}/memory-markers/${encodeURIComponent(markerId)}`);
}

export async function uploadMemoryMarkerImage(markerId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axios.post(
    `${apiBase()}/memory-markers/${encodeURIComponent(markerId)}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteMemoryMarkerImage(markerId) {
  await axios.delete(`${apiBase()}/memory-markers/${encodeURIComponent(markerId)}/image`);
}

/** Best delivery URL for cards (upload pipeline or legacy URL). */
export function getMarkerDisplayImage(marker) {
  if (!marker) return null;
  const urls = marker.image_urls;
  if (urls?.card) return urls.card;
  if (urls?.thumb) return urls.thumb;
  return marker.image_url || null;
}

export function buildPersonAttachment(personId) {
  return { mode: 'person', person_id: personId, person_id_2: null, partnership_id: null };
}

export function buildTimelineOnlyAttachment() {
  return { mode: 'timeline_only', person_id: null, person_id_2: null, partnership_id: null };
}

export function parseTagsInput(raw) {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 12);
}

export function formatTagsForInput(tags) {
  if (!Array.isArray(tags) || tags.length === 0) return '';
  return tags.join(', ');
}

export function groupMarkersByType(markers) {
  const groups = {};
  for (const marker of markers || []) {
    const type = marker.marker_type || 'event';
    if (!groups[type]) groups[type] = [];
    groups[type].push(marker);
  }
  return MARKER_TYPE_ORDER.filter((t) => groups[t]?.length).map((type) => ({
    type,
    label: MARKER_TYPE_LABELS[type],
    items: groups[type],
  }));
}

export function getMarkerTypeStyle(markerType) {
  return MARKER_TYPE_STYLES[markerType] || MARKER_TYPE_STYLES.event;
}

export function getMarkerTypeIcon(markerType) {
  return MARKER_TYPE_ICONS[markerType] || Package;
}

export function partOfStoryLine(anchorPerson) {
  if (!anchorPerson) return null;
  const given = (anchorPerson.given_names || '').trim().split(/\s+/)[0];
  if (!given) return null;
  const possessive = given.endsWith('s') ? `${given}'` : `${given}'s`;
  return `Part of ${possessive} story`;
}

export function isPricelessPossession(marker) {
  return POSSESSION_MARKER_TYPES.includes(marker?.marker_type);
}

export function possessionWhyLine(marker) {
  return (marker?.why_it_matters || marker?.description || '').trim();
}

export function filterPossessions(markers) {
  return (markers || []).filter(isPricelessPossession);
}

export function filterNonPossessions(markers) {
  return (markers || []).filter((m) => !isPricelessPossession(m));
}

export function possessionEmptyPayload({ personId = null, primaryPersonId = null } = {}) {
  const pid = primaryPersonId || personId;
  return {
    marker_type: 'keepsake',
    title: '',
    description: '',
    why_it_matters: '',
    story_text: '',
    category: 'other',
    approx_date: '',
    location_label: '',
    attachment: pid ? buildPersonAttachment(pid) : buildTimelineOnlyAttachment(),
    connected_person_ids: pid ? [pid] : [],
    primary_person_id: pid || null,
    linked_story_id: null,
    voice_note_id: null,
    privacy: 'private',
    include_in_pdf: true,
    include_in_memorial: false,
    tags: [],
  };
}

export function markerEmptyPayload({ personId = null, attachmentMode = 'person' } = {}) {
  return {
    marker_type: 'place',
    title: '',
    description: '',
    approx_date: '',
    attachment:
      attachmentMode === 'timeline_only' || !personId
        ? buildTimelineOnlyAttachment()
        : buildPersonAttachment(personId),
    linked_story_id: null,
    image_url: '',
    location_label: '',
    tags: [],
  };
}
